import { FC, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Transition } from '@headlessui/react';

import {
  NotificationFrequency,
  PatientDataPermission,
  useMeProviderUserNotificationControlsLazyQuery,
  useUpdateProviderUserNotificationControlsMutation,
} from '../../../generated/graphql';
import Spinner from '../../svgs/Spinner';

import ToastAlert from '../../components/ToastAlert';
import { useAuth } from '../../../contexts/AuthContext';
import { defaultTransitionProps } from '../../lib/animation';
import {
  ChatIcon,
  DocumentReportIcon,
  UserIcon,
  UserGroupIcon,
  UsersIcon,
  CollectionIcon,
} from '@heroicons/react/outline';
import { SvgIconComponent } from '../../types/svgs';
import BoxCheck from '../../svgs/BoxCheck';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import debounce from 'lodash.debounce';

const FORM_DEBOUNCE_MS = 50;

interface NotificationControlsFormData {
  patientActivationEmailEnabled: boolean;
  patientCommentEmailEnabled: boolean;
  patientCommentEmailOption: NotificationFrequency | null;
  patientActivityCompletionEmailEnabled: boolean;
  patientAssessmentCompletionEmailEnabled: boolean;
  careTeamNotificationsEnabled: boolean;
  allClientsFormCompletionEnabled: boolean;
}

const NotificationsSettingSubSection: FC<{
  name: string;
  description: string;
  Icon: SvgIconComponent;
  emailEnabledFormFieldName: keyof NotificationControlsFormData;
  emailOptionFormFieldName?: keyof NotificationControlsFormData;
  defaultEmailOption: NotificationFrequency;
  checkboxLabel?: string;
  disabled?: boolean;
  disabledMessage?: string;
  toggleAlign?: 'below' | 'end';
}> = ({
  name,
  description,
  Icon,
  emailEnabledFormFieldName,
  emailOptionFormFieldName,
  defaultEmailOption,
  checkboxLabel = 'Email notification',
  disabled = false,
  disabledMessage,
  toggleAlign = 'below',
}) => {
  const { register, watch, setValue } =
    useFormContext<NotificationControlsFormData>();

  const emailEnabled = watch(emailEnabledFormFieldName);

  const emailEnabledInputId = `${emailEnabledFormFieldName}Input`;
  const emailDailyDigestOptionInputId = `${emailOptionFormFieldName}DailyDigestInput`;
  const emailRealTimeOptionInputId = `${emailOptionFormFieldName}RealTimeInput`;
  const alignToggleEnd = toggleAlign === 'end';

  const titleAndDescription = (
    <div>
      <div className="mb-1 text-caption font-medium text-green-150">{name}</div>
      <div className="text-small-caption text-neutral-125">{description}</div>
    </div>
  );

  const checkbox = (
    <div className="flex shrink-0 items-center">
      <input
        id={emailEnabledInputId}
        type="checkbox"
        disabled={disabled}
        className="mr-2 h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125 disabled:cursor-not-allowed disabled:opacity-60"
        {...register(emailEnabledFormFieldName, {
          onChange: (e) => {
            if (e.target.checked && emailOptionFormFieldName) {
              setValue(emailOptionFormFieldName, defaultEmailOption);
            }
          },
        })}
      />
      <label
        className={`text-caption text-neutral-125 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        htmlFor={emailEnabledInputId}
      >
        {checkboxLabel}
      </label>
    </div>
  );

  const disabledHint =
    disabled && disabledMessage ? (
      <div
        className={`mt-2 text-small-caption text-neutral-110 ${
          alignToggleEnd ? '' : 'ml-7'
        }`}
      >
        {disabledMessage}
      </div>
    ) : null;

  return (
    <div className="flex flex-row py-5">
      <div className="w-10 shrink-0">
        <Icon className="h-6 w-6 text-secondary-100" />
      </div>
      <div className="min-w-0 flex-1">
        {alignToggleEnd ? (
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              {titleAndDescription}
              {disabledHint}
            </div>
            {checkbox}
          </div>
        ) : (
          <>
            {titleAndDescription}
            <div className="mt-5">
              {checkbox}
              {disabledHint}
              {emailOptionFormFieldName && emailEnabled && !disabled && (
                <div className="ml-7">
                  <div className="mt-2 flex items-center">
                    <input
                      id={emailDailyDigestOptionInputId}
                      type="radio"
                      value={NotificationFrequency.DailyDigest}
                      className="mr-3 h-5 w-5 cursor-pointer rounded-full border-neutral-100 text-secondary-125 focus:ring-secondary-125"
                      {...register(emailOptionFormFieldName)}
                    />
                    <label
                      className="cursor-pointer"
                      htmlFor={emailDailyDigestOptionInputId}
                    >
                      <div className="text-caption text-neutral-125">
                        Email digest
                      </div>
                      <div className="text-small-caption text-neutral-110">
                        Get one email per day with all unread comments
                      </div>
                    </label>
                  </div>
                  <div className="mt-2 flex items-center">
                    <input
                      id={emailRealTimeOptionInputId}
                      type="radio"
                      value={NotificationFrequency.RealTime}
                      className="mr-3 h-5 w-5 cursor-pointer rounded-full border-neutral-100 text-secondary-125 focus:ring-secondary-125"
                      {...register(emailOptionFormFieldName)}
                    />
                    <label
                      className="cursor-pointer"
                      htmlFor={emailRealTimeOptionInputId}
                    >
                      <div className="text-caption text-neutral-125">
                        Email for each comment
                      </div>
                      <div className="text-small-caption text-neutral-110">
                        Get a real-time email for each comment you receive
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const NotificationsSection: FC = () => {
  const { authedProviderUser } = useAuth();
  const [
    notificationControlsQuery,
    {
      loading: isLoadingNotificationControls,
      error: notificationControlsError,
    },
  ] = useMeProviderUserNotificationControlsLazyQuery();

  const [updateProviderUserNotificationControls] =
    useUpdateProviderUserNotificationControlsMutation();

  const [hasMultipleTeamMembers, setHasMultipleTeamMembers] = useState(false);
  const hasMultipleTeamMembersRef = useRef(hasMultipleTeamMembers);
  hasMultipleTeamMembersRef.current = hasMultipleTeamMembers;

  const [hasAllClientAccess, setHasAllClientAccess] = useState(false);
  const hasAllClientAccessRef = useRef(hasAllClientAccess);
  hasAllClientAccessRef.current = hasAllClientAccess;

  const showAllClientsFormSetting =
    hasMultipleTeamMembers && hasAllClientAccess;
  const showAllClientsFormSettingRef = useRef(showAllClientsFormSetting);
  showAllClientsFormSettingRef.current = showAllClientsFormSetting;

  useEffect(() => {
    if (notificationControlsError) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong getting your notification controls. Please try refreshing."
          level="error"
        />
      ));
    }
  }, [notificationControlsError]);

  const formMethods = useForm<NotificationControlsFormData>({
    mode: 'onChange',
    defaultValues: async () => {
      const response = await notificationControlsQuery();
      const meProvider = response.data?.meProvider;
      const notificationControls = meProvider?.notificationControls;
      setHasMultipleTeamMembers(Boolean(meProvider?.hasMultipleTeamMembers));
      setHasAllClientAccess(
        meProvider?.patientDataPermission === PatientDataPermission.All,
      );
      return {
        patientActivationEmailEnabled: Boolean(
          notificationControls?.patientActivationEmailOption,
        ),
        patientCommentEmailEnabled: Boolean(
          notificationControls?.patientCommentEmailOption,
        ),
        patientCommentEmailOption:
          notificationControls?.patientCommentEmailOption ?? null,
        patientActivityCompletionEmailEnabled: Boolean(
          notificationControls?.patientActivityCompletionEmailOption,
        ),
        patientAssessmentCompletionEmailEnabled: Boolean(
          notificationControls?.patientAssessmentCompletionEmailOption,
        ),
        careTeamNotificationsEnabled: Boolean(
          notificationControls?.careTeamNotificationsEnabled,
        ),
        allClientsFormCompletionEnabled: Boolean(
          notificationControls?.allClientsFormCompletionEnabled,
        ),
      };
    },
  });

  const previousFormValues = useRef<NotificationControlsFormData | null>(null); // keep track of previous form values

  const handleSubmit = formMethods.handleSubmit;
  const watch = formMethods.watch;

  // Debounce form submission to prevent double-send on checkbox change + radio change
  const saveForm = debounce(
    async (formData: NotificationControlsFormData) => {
      // Only handle form change if formValues have changed from previous form values
      if (
        previousFormValues.current !== null &&
        formData !== previousFormValues.current
      ) {
        try {
          await updateProviderUserNotificationControls({
            variables: {
              input: {
                patientActivationEmailOption:
                  formData.patientActivationEmailEnabled
                    ? NotificationFrequency.RealTime
                    : null,
                patientCommentEmailOption:
                  formData.patientCommentEmailEnabled &&
                  formData.patientCommentEmailOption
                    ? formData.patientCommentEmailOption
                    : null,
                patientActivityCompletionEmailOption:
                  formData.patientActivityCompletionEmailEnabled
                    ? NotificationFrequency.RealTime
                    : null,
                patientAssessmentCompletionEmailOption:
                  formData.patientAssessmentCompletionEmailEnabled
                    ? NotificationFrequency.RealTime
                    : null,
                careTeamNotificationsEnabled: hasMultipleTeamMembersRef.current
                  ? formData.careTeamNotificationsEnabled
                  : false,
                ...(showAllClientsFormSettingRef.current
                  ? {
                      allClientsFormCompletionEnabled:
                        formData.allClientsFormCompletionEnabled,
                    }
                  : {}),
              },
            },
          });
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              message="Changes to your notification settings were successfully saved"
              level="success"
            />
          ));
        } catch (error) {
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              message="Something went wrong saving your notification settings. Please try again."
              level="error"
            />
          ));
        }
      }
      // Update previous form values to current form values
      previousFormValues.current = formData;
    },
    FORM_DEBOUNCE_MS,
    {
      leading: false,
      trailing: true,
    },
  );

  useEffect(() => {
    // Callback form of watch (https://react-hook-form.com/api/useform/watch/)
    const subscription = watch(() => handleSubmit(saveForm)());
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const isExternalCollaborator = authedProviderUser?.isExternalCollaborator;

  return (
    <div className="mt-2 flex w-full max-w-[1100px] flex-col pb-10">
      {isExternalCollaborator && (
        <div className="mb-3 flex rounded-md bg-orange-25/30 p-4 text-orange-125">
          <UsersIcon
            className="h-5 min-h-[20px] w-5 min-w-[20px] text-orange-100"
            aria-hidden="true"
          />
          <span className="ml-3 font-sans text-caption font-medium">
            Update notification settings
            {authedProviderUser?.provider?.name
              ? ` for ${authedProviderUser.provider.name}`
              : ''}
          </span>
        </div>
      )}
      <Transition
        show={!isLoadingNotificationControls && !notificationControlsError}
        {...defaultTransitionProps}
      >
        <form>
          <FormProvider {...formMethods}>
            <div className="flex flex-col mdlg:flex-row mdlg:items-start mdlg:gap-x-12">
              <div className="min-w-0 flex-1 divide-y divide-neutral-75 mdlg:max-w-[555px]">
                <NotificationsSettingSubSection
                  name="Activations"
                  description="Be notified when clients sign up to work with you through your Homecoming webpage or activate on the mobile app."
                  Icon={UserIcon}
                  emailEnabledFormFieldName="patientActivationEmailEnabled"
                  defaultEmailOption={NotificationFrequency.RealTime}
                />
                <NotificationsSettingSubSection
                  name="Comments"
                  description="Be notified when a client replies to a follow-up."
                  Icon={ChatIcon}
                  emailEnabledFormFieldName="patientCommentEmailEnabled"
                  emailOptionFormFieldName="patientCommentEmailOption"
                  defaultEmailOption={NotificationFrequency.DailyDigest}
                />
                <NotificationsSettingSubSection
                  name="Activity completions"
                  description="Be notified when a client completes an action or a resource-based activity."
                  Icon={BoxCheck}
                  emailEnabledFormFieldName="patientActivityCompletionEmailEnabled"
                  defaultEmailOption={NotificationFrequency.RealTime}
                />
                <NotificationsSettingSubSection
                  name="Form and Assessment completions"
                  description="Be notified when a client completes a form or assessment."
                  Icon={DocumentReportIcon}
                  emailEnabledFormFieldName="patientAssessmentCompletionEmailEnabled"
                  defaultEmailOption={NotificationFrequency.RealTime}
                />
              </div>
              <div className="min-w-0 flex-1 divide-y divide-neutral-75 border-t border-neutral-75 mdlg:max-w-[555px] mdlg:border-t-0 mdlg:border-l mdlg:pl-12">
                <NotificationsSettingSubSection
                  name="Care Team"
                  description="Be notified when a client you're assigned to completes an action, resource-based activity, form, or assessment. For care team members who didn't send the follow-up with those activities."
                  Icon={UserGroupIcon}
                  emailEnabledFormFieldName="careTeamNotificationsEnabled"
                  defaultEmailOption={NotificationFrequency.RealTime}
                  checkboxLabel="Notify me"
                  toggleAlign="end"
                  disabled={!hasMultipleTeamMembers}
                  disabledMessage={
                    !hasMultipleTeamMembers
                      ? 'Care team notifications are only available when there are multiple active members on your team.'
                      : undefined
                  }
                />
                {showAllClientsFormSetting && (
                  <NotificationsSettingSubSection
                    name="All clients I have access to"
                    description="Be notified when a client you have access to completes a form or assessment, even if you aren't assigned to them."
                    Icon={CollectionIcon}
                    emailEnabledFormFieldName="allClientsFormCompletionEnabled"
                    defaultEmailOption={NotificationFrequency.RealTime}
                    checkboxLabel="Notify me"
                    toggleAlign="end"
                  />
                )}
              </div>
            </div>
          </FormProvider>
        </form>
      </Transition>

      {isLoadingNotificationControls && <Spinner className="mx-auto mt-12" />}
    </div>
  );
};

export default NotificationsSection;
